import React from 'react';
import { motion } from 'framer-motion';
import { 
  Flame, 
  TrendingUp, 
  Leaf, 
  Droplets, 
  Zap, 
  TreePine,
  Calendar,
  ChevronRight,
  Play
} from 'lucide-react';

export default function Dashboard() {
  const impactStats = [
    { icon: Leaf, label: 'CO₂ Saved', value: '45.2', unit: 'kg', color: 'text-green-600', bg: 'bg-green-100' },
    { icon: Droplets, label: 'Water Saved', value: '1,240', unit: 'L', color: 'text-blue-600', bg: 'bg-blue-100' },
    { icon: Zap, label: 'Energy Saved', value: '89.5', unit: 'kWh', color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { icon: TreePine, label: 'Trees Planted', value: '3', unit: '', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  ];

  const todaysMissions = [
    {
      id: 1,
      title: 'Bring Your Own Bag',
      description: 'Use reusable bags for shopping',
      points: 50,
      difficulty: 'Easy',
      icon: '🛍️',
      progress: 0
    },
    {
      id: 2,
      title: 'Plant-Based Meal',
      description: 'Share a photo of your meat-free meal',
      points: 75,
      difficulty: 'Easy',
      icon: '🥗',
      progress: 0
    },
    {
      id: 3,
      title: 'Car-Free Commute',
      description: 'Walk, bike, or use public transport',
      points: 100,
      difficulty: 'Medium',
      icon: '🚲',
      progress: 0
    }
  ];

  return (
    <div className="h-full mobile-scroll custom-scrollbar">
      <div className="p-4 space-y-6 pb-6">
        {/* Welcome Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white card-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">Welcome back! 🌱</h2>
              <p className="text-emerald-100">Ready to make an impact?</p>
            </div>
            <img
              src="https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Profile"
              className="w-16 h-16 rounded-2xl border-3 border-white/20"
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <Flame className="h-5 w-5 text-orange-300" />
              </div>
              <p className="text-xl font-bold">12</p>
              <p className="text-xs text-emerald-100">Day Streak</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <TrendingUp className="h-5 w-5 text-blue-300" />
              </div>
              <p className="text-xl font-bold">Tree</p>
              <p className="text-xs text-emerald-100">Level</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
              </div>
              <p className="text-xl font-bold">1,247</p>
              <p className="text-xs text-emerald-100">EcoCoins</p>
            </div>
          </div>
        </motion.div>

        {/* Impact Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Your Environmental Impact</h3>
          <div className="grid grid-cols-2 gap-4">
            {impactStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: 0.1 + index * 0.05 }}
                  className="bg-white rounded-2xl p-4 card-shadow"
                >
                  <div className={`inline-flex p-2 rounded-xl ${stat.bg} mb-3`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mb-1">
                    {stat.value}
                    <span className="text-sm font-normal text-gray-500 ml-1">{stat.unit}</span>
                  </p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Today's Missions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-6 w-6 text-emerald-600" />
              <h3 className="text-xl font-bold text-gray-900">Today's Missions</h3>
            </div>
            <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
              {todaysMissions.length} available
            </span>
          </div>

          <div className="space-y-3">
            {todaysMissions.map((mission, index) => (
              <motion.div
                key={mission.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.2 + index * 0.1 }}
                className="bg-white rounded-2xl p-4 card-shadow"
              >
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">{mission.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-gray-900">{mission.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        mission.difficulty === 'Easy' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {mission.difficulty}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{mission.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-emerald-600">
                        +{mission.points} EcoCoins
                      </span>
                      <button className="bg-emerald-500 text-white px-4 py-2 rounded-xl font-medium flex items-center space-x-1 ripple">
                        <Play className="h-4 w-4" />
                        <span>Start</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white rounded-2xl p-4 card-shadow"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
              <span className="font-medium text-gray-700">View All Missions</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            <button className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
              <span className="font-medium text-gray-700">Leaderboard</span>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}